// MARK://dhk8/admin-shell/lib/clients/roles
// Roles del tenant (#3 F4) → dhk8-platform-auth por el grafo:
//   roles                        → built-ins sintéticos (builtin:true) + custom (builtin:false)
//   createRole / updateRole / deleteRole  (acciones de AUTORIDAD; el backend rechaza
//                                          mutar built-ins → la UI los muestra read-only)
// Un rol = bundle de MÓDULOS visibles (claves canónicas de @dockethub/contracts).
// El tenant activo viaja en x-dhk8-tenant-id (lo inyecta gqlClient desde la sesión).
// Preview (VITE_API_STUB=1): built-ins derivados del catálogo + store mutable de custom.
import { BUILTIN_ROLE_MODULES, MODULE_KEYS, type BuiltinRole } from '@dockethub/contracts/shared'
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import {
  RolesQuery, CreateRoleMutation, UpdateRoleMutation, DeleteRoleMutation,
} from '../queries/roles.queries.js'

export interface Role {
  id: string
  name: string
  description: string | null
  modules: string[]
  builtin: boolean
}
export interface RoleInput {
  name: string
  description?: string | null
  modules: string[]
}

// ── preview store (no backend) ───────────────────────────────────────────────
// Built-ins sintéticos a partir del catálogo canónico; custom en un array mutable.
const PREVIEW_BUILTIN_LABEL: Record<BuiltinRole, string> = {
  owner: 'Dueño', manager: 'Gerente', cashier: 'Cajero', waiter: 'Mesero', kitchen: 'Cocina',
}
function previewBuiltins(): Role[] {
  return (Object.keys(PREVIEW_BUILTIN_LABEL) as BuiltinRole[]).map((r) => {
    const set = BUILTIN_ROLE_MODULES[r]
    return {
      id: `builtin:${r}`,
      name: r,
      description: PREVIEW_BUILTIN_LABEL[r],
      modules: set === '*' ? [...MODULE_KEYS] : [...set],
      builtin: true,
    }
  })
}
const previewCustom: Role[] = []
const mkid = () => `role-${Math.random().toString(36).slice(2, 8)}`

// ── reads ────────────────────────────────────────────────────────────────────
export async function listRoles(signal?: AbortSignal): Promise<Role[]> {
  if (PREVIEW_STUB) return mock([...previewBuiltins(), ...previewCustom])
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(RolesQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.roles ?? []).map((r): Role => ({
    id: r.id, name: r.name, description: r.description ?? null, modules: [...r.modules], builtin: r.builtin,
  }))
}

// ── writes ───────────────────────────────────────────────────────────────────
export async function createRole(input: RoleInput): Promise<Role> {
  if (PREVIEW_STUB) {
    const role: Role = { id: mkid(), name: input.name, description: input.description ?? null, modules: [...input.modules], builtin: false }
    previewCustom.push(role)
    return mock(role)
  }
  const res = await gqlClient.mutation(CreateRoleMutation, {
    input: {
      name: input.name,
      ...(input.description != null ? { description: input.description } : {}),
      modules: input.modules,
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createRole
  if (!r) throw new Error('No se pudo crear el rol')
  return { id: r.id, name: r.name, description: r.description ?? null, modules: [...r.modules], builtin: r.builtin }
}

export async function updateRole(id: string, input: Partial<RoleInput>): Promise<Role> {
  if (PREVIEW_STUB) {
    const role = previewCustom.find((x) => x.id === id)
    if (role) {
      if (input.name !== undefined) role.name = input.name
      if (input.description !== undefined) role.description = input.description ?? null
      if (input.modules !== undefined) role.modules = [...input.modules]
    }
    if (!role) throw new Error('Rol no encontrado')
    return mock({ ...role })
  }
  const res = await gqlClient.mutation(UpdateRoleMutation, {
    id,
    input: {
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.description !== undefined ? { description: input.description } : {}),
      ...(input.modules !== undefined ? { modules: input.modules } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateRole
  if (!r) throw new Error('No se pudo actualizar el rol')
  return { id: r.id, name: r.name, description: r.description ?? null, modules: [...r.modules], builtin: r.builtin }
}

export async function deleteRole(id: string): Promise<boolean> {
  if (PREVIEW_STUB) {
    const i = previewCustom.findIndex((x) => x.id === id)
    if (i >= 0) previewCustom.splice(i, 1)
    return mock(true)
  }
  const res = await gqlClient.mutation(DeleteRoleMutation, { id }).toPromise()
  if (res.error) throw res.error
  return res.data?.deleteRole ?? false
}
